import { filesUploaderService } from "../../services/files-uploader-service";

export class GeneralMultiPartUploader {
  file: File;
  fileName: string;
  fileId: string;
  category: string;
  clientId: string;
  folderId: string;
  chunkSize: number;
  threadsQuantity: number;
  contentType: string;
  aborted = false;
  uploadedSize = 0;
  progressCache: any = {};
  activeConnections: any = {};
  parts: any[] = [];
  uploadedParts: any = [];
  uploadId: string = "";
  fileKey: string = "";
  uniqueUploadedFileName: string = "";
  getProgressFunction: ((params: any) => void) | undefined;
  getErrorFunction: ((error: any) => void) | undefined;
  getCompleteFunction: ((params: any) => void) | undefined;

  constructor(options: {
    file: File;
    fileName: string;
    fileId: string;
    category: string;
    clientId: string;
    folderId?: string;
    chunkSize?: number;
    threadsQuantity?: number;
  }) {
    this.file = options.file;
    this.contentType = options.file.type;
    this.fileName = options.fileName;
    this.fileId = options.fileId;
    this.category = options.category;
    this.clientId = options.clientId;
    this.folderId = options.folderId || "ROOT";
    this.chunkSize = options.chunkSize || 1024 * 1024 * 5;
    this.threadsQuantity = Math.min(options.threadsQuantity || 5, 15);
  }

  async start() {
    await this.initialize();
  }

  async initialize() {
    try {
      const initResponse = await filesUploaderService.initializeFileUpload({
        fileName: this.fileName,
        fileId: this.fileId,
        contentType: this.contentType,
        category: this.category,
      });

      this.uploadId = initResponse.uploadId;
      this.fileKey = initResponse.fileKey;
      this.uniqueUploadedFileName = initResponse.fileName;

      const numberOfParts = Math.ceil(this.file.size / this.chunkSize);

      const presignedUrlResponse = await filesUploaderService.createFilePresignedUrls({
        uploadId: this.uploadId,
        fileKey: this.fileKey,
        parts: numberOfParts,
      });

      this.parts.push(...presignedUrlResponse);
      this.sendNext();
    } catch (err) {
      console.log("Error initializing file upload", err);
      if (this.getErrorFunction) this.getErrorFunction(err);
    }
  }

  sendNext() {
    const activeConnections = Object.keys(this.activeConnections).length;
    if (activeConnections >= this.threadsQuantity) return;
    if (!this.parts.length) {
      if (!activeConnections) this.complete();
      return;
    }

    const part = this.parts.pop();
    if (this.file && part) {
      const sentSize = (part.PartNumber - 1) * this.chunkSize;
      const chunk = this.file.slice(sentSize, sentSize + this.chunkSize);

      const sendChunkStarted = () => this.sendNext();

      this.sendChunk(chunk, part, sendChunkStarted)
        .then(() => this.sendNext())
        .catch((error) => {
          this.parts.push(part);
          this.complete(error);
        });
    }
  }

  sendChunk(chunk: Blob, part: any, sendChunkStarted: () => void): Promise<void> {
    return new Promise((resolve, reject) => {
      this.upload(chunk, part, sendChunkStarted)
        .then((status) => {
          if (status !== 200) {
            reject(new Error("Failed chunk upload"));
            return;
          }
          resolve();
        })
        .catch(reject);
    });
  }

  upload(chunk: Blob, part: any, sendChunkStarted: () => void): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.uploadId && this.fileKey) {
        const xhr = (this.activeConnections[part.PartNumber - 1] = new XMLHttpRequest());
        sendChunkStarted();

        const progressListener = this.handleProgress.bind(this, part.PartNumber - 1);
        xhr.upload.addEventListener("progress", progressListener);
        xhr.addEventListener("error", progressListener);
        xhr.addEventListener("abort", progressListener);
        xhr.addEventListener("loadend", progressListener);

        xhr.open("PUT", part.signedUrl);

        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4 && xhr.status === 200) {
            const ETag = xhr.getResponseHeader("ETag");
            if (ETag) {
              this.uploadedParts.push({
                PartNumber: part.PartNumber,
                ETag: ETag.replaceAll('"', ""),
              });
              resolve(xhr.status);
              delete this.activeConnections[part.PartNumber - 1];
            }
          }
        };

        xhr.onerror = (error) => {
          reject(error);
          delete this.activeConnections[part.PartNumber - 1];
        };

        xhr.onabort = () => {
          reject(new Error("Upload canceled by user"));
          delete this.activeConnections[part.PartNumber - 1];
        };

        xhr.send(chunk);
      }
    });
  }

  handleProgress(part: any, event: any) {
    if (this.file) {
      if (event.type === "progress" || event.type === "error" || event.type === "abort") {
        this.progressCache[part] = event.loaded;
      }
      if (event.type === "uploaded") {
        this.uploadedSize += this.progressCache[part] || 0;
        delete this.progressCache[part];
      }

      const inProgress = Object.keys(this.progressCache)
        .map(Number)
        .reduce((memo, id) => (memo += this.progressCache[id]), 0);

      const sent = Math.min(this.uploadedSize + inProgress, this.file.size);
      const total = this.file.size;
      const percentage = Math.round((sent / total) * 100);

      if (this.getProgressFunction) {
        this.getProgressFunction({ sent, total, percentage });
      }
    }
  }

  async complete(error?: any) {
    if (error) {
      if (this.getErrorFunction) this.getErrorFunction(error);
      return;
    }

    try {
      await this.sendCompleteRequest();
    } catch (err) {
      if (this.getErrorFunction) this.getErrorFunction(err);
    }
  }

  async sendCompleteRequest() {
    if (this.uploadId && this.fileKey) {
      await filesUploaderService.finalizeFileUpload({
        uploadId: this.uploadId,
        fileKey: this.fileKey,
        parts: this.uploadedParts,
      });

      await filesUploaderService.createFileMetadata({
        fileId: this.fileId,
        originalFilename: this.fileName,
        s3Key: this.fileKey,
        objectSize: this.file.size,
        mimeType: this.contentType,
        category: this.category,
        folderId: this.folderId,
      });

      if (this.getCompleteFunction) {
        this.getCompleteFunction({
          fileId: this.fileId,
          fileName: this.fileName,
          s3Key: this.fileKey,
        });
      }
    }
  }

  abort() {
    Object.keys(this.activeConnections)
      .map(Number)
      .forEach((id) => this.activeConnections[id].abort());
    this.aborted = true;
  }

  getOnProgress(fn: (params: any) => void) {
    this.getProgressFunction = fn;
    return this;
  }

  getOnError(fn: (error: any) => void) {
    this.getErrorFunction = fn;
    return this;
  }

  getOnComplete(fn: (params: any) => void) {
    this.getCompleteFunction = fn;
    return this;
  }
}
